import React, { createContext, useEffect, useState } from 'react';
import { IPanelStyles, Panel, PanelType } from '@fluentui/react';
import { useTranslation } from 'react-i18next';
import useWindowWidth from '../tikit/useWindowWidth';

interface Props {
  isOpen: boolean;
  onDismiss: () => void;
  onOuterClick?: () => void;
  className?: string;
  children: React.ReactNode;
}

// On desktop the user page draws its own close button in its pinned header, so the panel's navigation bar
// and padding go and the page sits flush to the panel's edges, as it does under the users breadcrumb.
const DESKTOP_STYLES: Partial<IPanelStyles> = {
  commands: { display: 'none' },
  content: { padding: 0 },
};

// A phone keeps the close bar but not the side padding, so the page inside has the same edges as the full user page
// (its own 20px) - the Tickets tab's cards then line up with every other phone card list.
const PHONE_STYLES: Partial<IPanelStyles> = {
  content: { paddingLeft: 0, paddingRight: 0 },
};

// The user page tells the panel when it is drawing its own close button (UserForm, once the user has loaded). Until
// then - loading, signing in, an error - the panel keeps its own, so there is never a panel with no way to close it.
export const UserPanelCloseContext = createContext<(pageCloses: boolean) => void>(() => { });

/**
 * The side panel a user page opens in from a ticket - the requester card and the names in the activity.
 * A phone keeps the panel's own close bar, since there the whole page scrolls.
 */
export const UserPanel = ({ isOpen, onDismiss, onOuterClick, className, children }: Props) => {
  const { t } = useTranslation();
  const isPhone = useWindowWidth() < 768;
  const [pageDrawsClose, setPageDrawsClose] = useState(false);
  useEffect(() => { if (!isOpen) setPageDrawsClose(false); }, [isOpen]);
  const pageCloses = !isPhone && pageDrawsClose;
  let styles: Partial<IPanelStyles> | undefined;
  if (pageCloses) styles = DESKTOP_STYLES;
  else if (isPhone) styles = PHONE_STYLES;

  return (
    <Panel
      isLightDismiss
      isOpen={isOpen}
      onDismiss={onDismiss}
      onOuterClick={onOuterClick}
      closeButtonAriaLabel={t('common.buttons.close')}
      type={PanelType.large}
      className={className}
      hasCloseButton={!pageCloses}
      styles={styles}
    >
      <UserPanelCloseContext.Provider value={setPageDrawsClose}>{children}</UserPanelCloseContext.Provider>
    </Panel>
  );
};
